import React, { useState, useEffect } from 'react'
import { compose } from 'recompose'
import { Auth } from 'aws-amplify'
import { Route, Switch, Router, Redirect } from 'react-router-dom'
import { history } from 'config/history'
import { useTranslation } from 'react-i18next'
import get from 'lodash/get'
import { useApolloClient } from '@apollo/client'
import { usePostHog } from '@posthog/react'
import moment from 'moment'
import 'moment/locale/de'
import 'moment/locale/en-gb'
import 'moment/locale/fr'
import 'moment/locale/it'

import getSections, { getMainNavContent, getMainSectionsRegexp, getBottomSections } from 'sections/sections'
import MediaTest from 'sections/media/pages/MediaTest'
import NavBar from 'components/NavBar'
import SectionHandler from 'components/SectionHandler'
import PageNotFound from 'components/PageNotFound'
import { AlertModal } from 'components/Modal'

import withAuth, { isAdmin } from 'components/withAuth'
import withAppInit from 'components/withAppInit'
import ErrorHandler from 'components/ErrorHandler'
import propsChangeCheck from 'helpers/propsChangeCheck'

import 'react-table/react-table.css'
import 'weather-icons/css/weather-icons.min.css'
import { StyledAppContainer } from './style/styled'
import { version } from '../package.json'

// ISO 8601 week: Monday is the first day of the week, week 1 contains Jan 4th.
// Written as individual calls (not [...].forEach) because Vite's tree-shaker
// drops the latter as a side-effect-free expression.
const ISO_WEEK = { week: { dow: 1, doy: 4 } }
moment.updateLocale('en', ISO_WEEK)
moment.updateLocale('en-gb', ISO_WEEK)
moment.updateLocale('de', ISO_WEEK)
moment.updateLocale('fr', ISO_WEEK)
moment.updateLocale('it', ISO_WEEK)
moment.locale('de')

// eslint-disable-next-line no-console
console.log('App version:', version)

const App = ({ onStateChange, initData = {}, resetCache, authData, ...rest }) => {
	const client = useApolloClient()
	const [t, i18n] = useTranslation()
	const posthog = usePostHog()
	const [acknowledgeLicenseError, setAcknowledgeLicenseError] = useState(false)

	const appData = {
		...get(initData, 'cashAssist.getAppConfig', {}),
		appConfigAdditional: get(initData, 'cashAssist.getAppConfigAdditional', {}),
		isAdmin: isAdmin(authData),
		authData,
		isMasterPrintActivated: get(initData, 'whoami.company.isMasterPrintActivated'),
		shopAccess: authData.attributes ? JSON.parse(authData.attributes['custom:access_shops'] || null) : null
	}

	const appLanguage = appData.language
	const companyName = appData.isAdmin ? get(initData, 'cashAssist.getAppConfig.name') : null
	const showNoLicenseError = !initData.whoami.company.modules.cashassist?.isActive || initData.whoami.company.isInactive

	if (appLanguage && appLanguage !== i18n.language) {
		i18n.changeLanguage(appLanguage)
	}

	const signOut = async () => {
		await resetCache()
		await Auth.signOut() // sign out user
		history.push('/')
	}

	const [accessRights, setAccessRights] = useState([])
	const getRights = async () => {
		const accessCustomRights = JSON.parse((await Auth.currentUserInfo()).attributes?.['custom:access_custom_rights'] || null)?.caw
		if (accessCustomRights) {
			setAccessRights(accessCustomRights?.map(e => e?.split(',')))
		}
	}
	useEffect(() => {
		getRights()
	}, [])
	useEffect(() => {
		posthog.identify(authData.attributes.sub, {
			email: authData.attributes.email
		})
	}, [posthog, authData.attributes.sub, authData.attributes.email])

	const bottomSections = getBottomSections({ signOut, companyName, t })

	return (
		<>
			{showNoLicenseError && !acknowledgeLicenseError &&
				<AlertModal
					width={340}
					text={t('common.strings.no_license_contact_support')}
					onClose={() => {
						if(appData.isAdmin) {
							setAcknowledgeLicenseError(true)
						} else {
							window.location.reload()
						}
					}}
				/>
			}
			{(!showNoLicenseError || appData.isAdmin) &&
				<Router history={history}>
					<ErrorHandler client={client}>
						<Switch>
							<Redirect exact from='/' to='/artikel'/>
							<Route exact path='/mediacenter/testen'><MediaTest/></Route>
							<Route path='/'><Main accessRights={accessRights} appData={appData} signOut={signOut} t={t} i18n={i18n} companyName={companyName} bottomSections={bottomSections}/></Route>
						</Switch>
					</ErrorHandler>
				</Router>
			}
		</>
	)
}

const Main = ({ appData, t, i18n, bottomSections, accessRights }) => {
	// const [t, i18n] = useTranslation()
	const sections = getSections(t, accessRights, i18n)
	const mainSectionsRegexp = getMainSectionsRegexp(sections)
	const mainNavContent = getMainNavContent(sections, t, appData?.name)
	return (
		<StyledAppContainer>
			<NavBar
				barHeight='mainSections'
				className='main'
				activeBorder
				navContent={mainNavContent}
				id='sectionContent'
			>
				<Switch>
					<Route path={`/:section(${mainSectionsRegexp})/:subsection?/:id?`}>
						<SectionHandler appData={appData} sections={sections} />
					</Route>
					<Route><PageNotFound/></Route>
				</Switch>
			</NavBar>
			<NavBar className='bottomnav' barHeight='bottomNav' navContent={bottomSections}/>
		</StyledAppContainer>
	)
}

export default compose(
	withAuth,
	withAppInit,
	propsChangeCheck(['initData'])
)(App)
